import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type DragEvent,
  type KeyboardEvent,
} from 'react'
import {
  Banner,
  Button,
  Card,
  Dropdown,
  HStack,
  IconButton,
  Text,
  VStack,
  useIsMobile,
} from 'wss3-forge'
import {
  Attach20Regular,
  ChevronDown16Regular,
  Dismiss12Regular,
  Document20Regular,
  Globe16Regular,
  LockClosed16Regular,
  Mic20Regular,
  Send20Regular,
} from '@fluentui/react-icons'
import { useSpeechRecognition } from '../hooks/useSpeechRecognition.js'
import { useIncognito } from '../contexts/IncognitoContext.js'
import type { Attachment } from '../lib/api.js'

export interface ChatComposerProps {
  disabled?: boolean
  onSend: (message: string, attachments?: Attachment[]) => void
  /**
   * Called when the user taps the mic affordance while the input is empty.
   * If provided, this opens the immersive voice mode overlay instead of
   * starting an inline dictation.
   */
  onVoiceMode?: () => void
  placeholder?: string
}

const MAX_FILES = 5
const MAX_TOTAL_BYTES = 10 * 1024 * 1024
const MAX_IMAGE_BYTES = 5 * 1024 * 1024
const MAX_TEXT_BYTES = 1 * 1024 * 1024

const IMAGE_MIMES = new Set([
  'image/png',
  'image/jpeg',
  'image/jpg',
  'image/webp',
  'image/gif',
])

const TEXT_EXTENSIONS = new Set([
  'txt', 'md', 'json', 'csv', 'ts', 'js', 'py', 'yaml', 'yml', 'html', 'css',
])

const ACCEPT_ATTR =
  'image/png,image/jpeg,image/webp,image/gif,' +
  '.txt,.md,.json,.csv,.ts,.js,.py,.yaml,.yml,.html,.css'

function extOf(name: string): string {
  const idx = name.lastIndexOf('.')
  if (idx === -1) return ''
  return name.slice(idx + 1).toLowerCase()
}

function isImageFile(file: File): boolean {
  if (IMAGE_MIMES.has(file.type)) return true
  const ext = extOf(file.name)
  return ext === 'png' || ext === 'jpg' || ext === 'jpeg' || ext === 'webp' || ext === 'gif'
}

function isTextFile(file: File): boolean {
  return TEXT_EXTENSIONS.has(extOf(file.name))
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} o`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} Ko`
  return `${(bytes / (1024 * 1024)).toFixed(1)} Mo`
}

/** Strip the `data:<mime>;base64,` prefix; the wire format keeps payload only. */
function readAsBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onerror = () => reject(reader.error ?? new Error('read failed'))
    reader.onload = () => {
      const result = typeof reader.result === 'string' ? reader.result : ''
      const commaIdx = result.indexOf(',')
      resolve(commaIdx === -1 ? result : result.slice(commaIdx + 1))
    }
    reader.readAsDataURL(file)
  })
}

interface PendingAttachment extends Attachment {
  /** Object URL used for the inline thumbnail. Revoked on removal. */
  previewUrl?: string
}

// Inject the composer-frame stylesheet once. We need :focus-within on the
// Card to lift the border to the brand color, and the raw <textarea>
// scrollbar styling. Done outside React so we don't leak <style> nodes per
// composer mount.
if (typeof document !== 'undefined' && !document.getElementById('stark-composer-styles')) {
  const styleEl = document.createElement('style')
  styleEl.id = 'stark-composer-styles'
  styleEl.textContent = `
    .stark-composer {
      transition: border-color 150ms ease, box-shadow 150ms ease;
    }
    .stark-composer:focus-within {
      border-color: var(--brand-primary);
      box-shadow: 0 0 0 1px var(--brand-primary);
    }
    .stark-composer.stark-composer-dragover {
      border-color: var(--brand-primary);
      box-shadow: 0 0 0 1px var(--brand-primary);
    }
    .stark-composer-textarea {
      width: 100%;
      border: 0;
      outline: 0;
      background: transparent;
      color: var(--text-primary);
      font: inherit;
      font-size: 15px;
      line-height: 1.5;
      resize: none;
      padding: 4px 4px 0;
      min-height: 28px;
      max-height: 240px;
      overflow-y: auto;
    }
    .stark-composer-textarea::placeholder {
      color: var(--text-muted);
    }
    .stark-composer-textarea::-webkit-scrollbar {
      width: 6px;
    }
    .stark-composer-textarea::-webkit-scrollbar-thumb {
      background-color: var(--border-default);
      border-radius: 3px;
    }
  `
  document.head.appendChild(styleEl)
}

/**
 * Unified chat composer (Claude-style). The Card is THE input surface.
 * - Top: a chrome-less textarea that auto-grows, sharing the same surface
 *   as the footer (no inner border, no inner background).
 * - Bottom row INSIDE the same card: mode picker (online vs Webba secure)
 *   on the left, single dynamic action button on the right (Mic when the
 *   composer is empty, Send when typing).
 *
 * We use a raw <textarea> rather than Forge Textarea because Forge ships
 * its own bordered/elevated chrome that fights with the Card frame. The
 * Card is the design-system primitive; the textarea inside is just text
 * input pixels with no visual identity of its own.
 */
export function ChatComposer({
  disabled = false,
  onSend,
  onVoiceMode,
  placeholder,
}: ChatComposerProps) {
  const [value, setValue] = useState('')
  const [attachments, setAttachments] = useState<PendingAttachment[]>([])
  const [attachError, setAttachError] = useState<string | null>(null)
  const [isDragging, setIsDragging] = useState(false)
  const isMobile = useIsMobile()
  const { incognito, setIncognito } = useIncognito()
  const textareaRef = useRef<HTMLTextAreaElement | null>(null)
  const fileInputRef = useRef<HTMLInputElement | null>(null)

  const handleFinalTranscript = useCallback((finalText: string) => {
    setValue((prev) => {
      const trimmedPrev = prev.trim()
      if (!trimmedPrev) return finalText
      return `${trimmedPrev} ${finalText}`
    })
  }, [])

  const stt = useSpeechRecognition({
    lang: 'fr-FR',
    continuous: false,
    interimResults: true,
    onFinalTranscript: handleFinalTranscript,
  })

  const trimmed = value.trim()
  const hasText = trimmed.length > 0
  const hasAttachments = attachments.length > 0
  const canSend = (hasText || hasAttachments) && !disabled

  // Auto-grow the textarea to fit its content, capped at 240px.
  useEffect(() => {
    const el = textareaRef.current
    if (!el) return
    el.style.height = 'auto'
    el.style.height = `${Math.min(el.scrollHeight, 240)}px`
  }, [value])

  // Initial focus on desktop only (mobile users don't want a forced keyboard).
  useEffect(() => {
    if (isMobile) return
    textareaRef.current?.focus()
  }, [isMobile])

  // Revoke any leftover object URLs so we don't leak across unmount.
  useEffect(() => {
    return () => {
      for (const a of attachments) {
        if (a.previewUrl) URL.revokeObjectURL(a.previewUrl)
      }
    }
    // We intentionally only run on unmount; per-attachment URLs are revoked
    // inline when the user removes a chip or after submit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const ingestFiles = useCallback(
    async (files: FileList | File[]) => {
      setAttachError(null)
      const incoming = Array.from(files)
      if (incoming.length === 0) return

      const accepted: PendingAttachment[] = []
      const rejected: string[] = []
      let runningTotal = attachments.reduce((sum, a) => sum + a.size, 0)
      let slotsLeft = MAX_FILES - attachments.length

      for (const file of incoming) {
        if (slotsLeft <= 0) {
          rejected.push(`${file.name} (limite de ${MAX_FILES} fichiers)`)
          continue
        }
        const isImage = isImageFile(file)
        const isText = isTextFile(file)
        if (!isImage && !isText) {
          rejected.push(`${file.name} (type non pris en charge)`)
          continue
        }
        const maxBytes = isImage ? MAX_IMAGE_BYTES : MAX_TEXT_BYTES
        if (file.size > maxBytes) {
          const cap = isImage ? '5 Mo' : '1 Mo'
          rejected.push(`${file.name} (max ${cap})`)
          continue
        }
        if (runningTotal + file.size > MAX_TOTAL_BYTES) {
          rejected.push(`${file.name} (total max 10 Mo)`)
          continue
        }
        try {
          const data = await readAsBase64(file)
          const mime = file.type || (isImage ? `image/${extOf(file.name)}` : 'text/plain')
          const previewUrl = isImage ? URL.createObjectURL(file) : undefined
          accepted.push({
            name: file.name,
            mime,
            data,
            size: file.size,
            ...(previewUrl ? { previewUrl } : {}),
          })
          runningTotal += file.size
          slotsLeft -= 1
        } catch {
          rejected.push(`${file.name} (lecture impossible)`)
        }
      }

      if (accepted.length > 0) {
        setAttachments((prev) => [...prev, ...accepted])
      }
      if (rejected.length > 0) {
        setAttachError(`Fichier(s) ignoré(s): ${rejected.join(', ')}`)
      }
    },
    [attachments],
  )

  const removeAttachment = (index: number): void => {
    setAttachments((prev) => {
      const target = prev[index]
      if (target?.previewUrl) URL.revokeObjectURL(target.previewUrl)
      return prev.filter((_, i) => i !== index)
    })
  }

  const onPickFiles = (): void => {
    fileInputRef.current?.click()
  }

  const onFileInputChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    if (e.target.files && e.target.files.length > 0) {
      void ingestFiles(e.target.files)
    }
    // Reset so re-picking the same file fires onChange again.
    e.target.value = ''
  }

  const submit = (): void => {
    if (!canSend) return
    if (stt.isListening) stt.abort()
    const wireAttachments: Attachment[] | undefined = hasAttachments
      ? attachments.map(({ previewUrl: _previewUrl, ...rest }) => rest)
      : undefined
    onSend(trimmed, wireAttachments)
    // Revoke after handing off; the caller has the base64 payload, not the URL.
    for (const a of attachments) {
      if (a.previewUrl) URL.revokeObjectURL(a.previewUrl)
    }
    setValue('')
    setAttachments([])
    setAttachError(null)
  }

  const onKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>): void => {
    // Enter sends, Shift+Enter inserts a newline. IME composition (e.g. JP
    // input) must not submit mid-composition.
    if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault()
      submit()
    }
  }

  const handleMicOrSend = (): void => {
    if (hasText || hasAttachments) {
      submit()
      return
    }
    if (onVoiceMode) {
      onVoiceMode()
      return
    }
    if (!stt.isSupported || disabled) return
    if (stt.isListening) stt.stop()
    else stt.start()
  }

  // Drag-and-drop directly onto the composer Card. We only flip the visual
  // hint while a payload that includes Files is over us.
  const onDragOver = (e: DragEvent<HTMLDivElement>): void => {
    if (!e.dataTransfer.types.includes('Files')) return
    e.preventDefault()
    setIsDragging(true)
  }
  const onDragLeave = (e: DragEvent<HTMLDivElement>): void => {
    // Ignore leaves that bubble from inner children; only react when we
    // actually exit the composer Card.
    if (e.currentTarget.contains(e.relatedTarget as Node | null)) return
    setIsDragging(false)
  }
  const onDrop = (e: DragEvent<HTMLDivElement>): void => {
    if (!e.dataTransfer.types.includes('Files')) return
    e.preventDefault()
    setIsDragging(false)
    const files = e.dataTransfer.files
    if (files && files.length > 0) void ingestFiles(files)
  }

  const dynamicPlaceholder = stt.error
    ? stt.error
    : stt.isListening
      ? stt.interim || 'Parle, je t’écoute...'
      : (placeholder ?? "Comment puis-je t'aider ?")

  const composerClass = `stark-composer${isDragging ? ' stark-composer-dragover' : ''}`

  return (
    <div onDragOver={onDragOver} onDragLeave={onDragLeave} onDrop={onDrop}>
      <Card variant="subtle" padding="md" className={composerClass}>
        <VStack gap="xs" align="stretch">
          {attachError && (
            <Banner
              variant="error"
              size="sm"
              showCloseButton
              onClose={() => setAttachError(null)}
            >
              {attachError}
            </Banner>
          )}

          {hasAttachments && (
            <HStack gap="xs" wrap align="center">
              {attachments.map((a, i) => (
                <Card key={`${a.name}-${i}`} variant="outlined" padding="xs">
                  <HStack gap="xs" align="center">
                    {a.previewUrl ? (
                      <img
                        src={a.previewUrl}
                        alt=""
                        style={{
                          width: 40,
                          height: 40,
                          objectFit: 'cover',
                          borderRadius: 'var(--radius-sm)',
                          flexShrink: 0,
                        }}
                      />
                    ) : (
                      <div
                        aria-hidden="true"
                        style={{
                          width: 40,
                          height: 40,
                          borderRadius: 'var(--radius-sm)',
                          backgroundColor: 'var(--bg-secondary)',
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'var(--text-muted)',
                          flexShrink: 0,
                        }}
                      >
                        <Document20Regular />
                      </div>
                    )}
                    <VStack gap="none" align="start">
                      <Text
                        size="xs"
                        style={{
                          maxWidth: 180,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {a.name}
                      </Text>
                      <Text size="xs" color="muted">
                        {formatBytes(a.size)}
                      </Text>
                    </VStack>
                    <IconButton
                      icon={<Dismiss12Regular />}
                      tooltip="Retirer"
                      aria-label={`Retirer ${a.name}`}
                      variant="ghost"
                      size="xs"
                      onClick={() => removeAttachment(i)}
                    />
                  </HStack>
                </Card>
              ))}
            </HStack>
          )}

          <textarea
            ref={textareaRef}
            className="stark-composer-textarea"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder={dynamicPlaceholder}
            disabled={disabled || stt.isListening}
            rows={1}
            aria-label="Message"
          />

          <HStack justify="between" align="center" gap="sm">
            <HStack gap="xs" align="center">
              {/* Mode picker. compact tightens the trigger padding so the
                  chip reads as a small inline affordance, not a chunky form
                  control. */}
              <Dropdown
                align="left"
                width={220}
                trigger={
                  <Button
                    variant="ghost"
                    size="sm"
                    compact
                    icon={incognito ? <LockClosed16Regular /> : <Globe16Regular />}
                    rightIcon={<ChevronDown16Regular />}
                  >
                    {incognito ? 'Sécurisé (1m)' : 'En ligne (2s)'}
                  </Button>
                }
                items={[
                  {
                    id: 'online',
                    label: 'En ligne (2s)',
                    icon: <Globe16Regular />,
                    onClick: () => setIncognito(false),
                  },
                  {
                    id: 'incognito',
                    label: 'Sécurisé Webba (1m)',
                    icon: <LockClosed16Regular />,
                    onClick: () => setIncognito(true),
                  },
                ]}
              />
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept={ACCEPT_ATTR}
                style={{ display: 'none' }}
                onChange={onFileInputChange}
              />
              <IconButton
                icon={<Attach20Regular />}
                tooltip="Joindre un fichier"
                aria-label="Joindre un fichier"
                variant="ghost"
                size="sm"
                disabled={disabled || attachments.length >= MAX_FILES}
                onClick={onPickFiles}
              />
            </HStack>
            {hasText || hasAttachments ? (
              <Button
                variant="primary"
                size="md"
                compact
                disabled={disabled}
                onClick={submit}
                aria-label="Envoyer le message"
              >
                <Send20Regular />
              </Button>
            ) : (
              <IconButton
                icon={<Mic20Regular />}
                tooltip={onVoiceMode ? 'Mode voix' : stt.isListening ? 'Arrêter la dictée' : 'Dicter un message'}
                aria-label={onVoiceMode ? 'Mode voix' : 'Dicter un message'}
                variant="ghost"
                size="md"
                onClick={handleMicOrSend}
              />
            )}
          </HStack>
        </VStack>
      </Card>
    </div>
  )
}
